import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FileText, Info } from "lucide-react";

/**
 * Admin · Letterhead options card.
 *
 * Compact panel that lets the admin flip the "show Vinci GSTIN / PAN" toggles
 * on every letterhead-bearing PDF (invoice · quotation · claim ack).
 * Off by default per board policy.
 */
export function LetterheadOptions({ api }) {
  const [opts, setOpts] = useState({ show_vinci_gstin: false, show_vinci_pan: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setOpts((await api.get("/admin/letterhead-options")).data || opts); }
    catch { toast.error("Could not load letterhead options"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/letterhead-options", next);
      setOpts(r.data);
      toast.success("Letterhead updated — takes effect on the next PDF generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const setFlag = (key) => (e) => save({ ...opts, [key]: e.target.checked });

  if (loading) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        data-testid="letterhead-options"
        className="border border-white/10 rounded-sm bg-[#0a0a0a] p-4">
      <div className="flex items-start gap-2 mb-3">
        <FileText size={14} className="text-[#B87333] mt-0.5" />
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">Letterhead options</div>
          <div className="font-body text-xs text-[#8A8A82]">
            Off by default. Turn on only when a specific contract needs Vinci GSTIN or PAN on the PDF.
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label data-testid="letterhead-opt-gstin"
               className="flex items-center gap-3 border border-white/10 rounded-sm p-3 cursor-pointer hover:border-[#B87333]/50">
          <input type="checkbox" checked={opts.show_vinci_gstin} onChange={setFlag("show_vinci_gstin")} disabled={saving}
                 className="accent-[#B87333] scale-110" />
          <div>
            <div className="font-body text-sm text-[#F5F5F0]">Show masked GSTIN</div>
            <div className="font-mono text-xs text-[#8A8A82]">2XXXMPD1XXXR1ZR</div>
          </div>
        </label>
        <label data-testid="letterhead-opt-pan"
               className="flex items-center gap-3 border border-white/10 rounded-sm p-3 cursor-pointer hover:border-[#B87333]/50">
          <input type="checkbox" checked={opts.show_vinci_pan} onChange={setFlag("show_vinci_pan")} disabled={saving}
                 className="accent-[#B87333] scale-110" />
          <div>
            <div className="font-body text-sm text-[#F5F5F0]">Show masked PAN</div>
            <div className="font-mono text-xs text-[#8A8A82]">XXABV701XX</div>
          </div>
        </label>
      </div>
      <div className="mt-3 flex items-center gap-1 text-[10px] text-[#8A8A82] font-body">
        <Info size={11}/> Applies to newly generated invoices, quotations, and claim acknowledgements.
      </div>
    </motion.div>
  );
}
